import { t } from '@trimble/i18n';
import {
  Measurement,
  AreaMeasurementType,
  MeasurementInfoButtonConfig,
} from '@trimble/web3d-providers';
import { createMemo, Show, type JSX } from 'solid-js';
import { AreaFormatter, LengthFormatter } from '@trimble/utilities';
import {
  MeasurementProperty,
  ResultRowProps,
} from '@trimble/measurements-shared';
import type { FormattingSettings } from '@trimble/types';
import { PolygonMeasurement } from '@technology/web3d-plugin-markup';
import { AreaUnit, LengthUnit } from '@fulcrum/lib';

export interface AreaMeasurementDetailsProps {
  measurement: Measurement | undefined;
  baseAreaUnit: AreaUnit;
  baseLengthUnit: LengthUnit;
  formattingSettings: FormattingSettings;
  infoButtonConfig?: MeasurementInfoButtonConfig;
}

export const AreaMeasurementDetails = (
  props: AreaMeasurementDetailsProps
): JSX.Element | null => {
  const measurementData = createMemo((): AreaMeasurementType | null => {
    if (!props.measurement || props.measurement.type !== 'area') return null;
    return props.measurement as AreaMeasurementType;
  });

  const areaData = createMemo((): ResultRowProps[] | null => {
    const data = measurementData();
    if (!data?.picks?.length || data.markups.length === 0) return null;
    const area = (data.markups[0] as PolygonMeasurement).area;
    const perimeter = (data.markups[0] as PolygonMeasurement).perimeter;

    return [
      {
        cells: [
          {
            label: t('measurements.areaMeasurement.area'),
            value: AreaFormatter.toDisplay({
              measuredValue: area,
              currentUnits: props.baseAreaUnit,
              settings: props.formattingSettings,
            }),
            testId: 'area-value',
            valueTestId: 'area-value-display',
          },
          {
            label: t('measurements.areaMeasurement.perimeter'),
            value: LengthFormatter.toDisplay({
              measuredValue: perimeter,
              currentUnits: props.baseLengthUnit,
              settings: props.formattingSettings,
              useMeasurementSettings: true,
            }),
            testId: 'perimeter-value',
            valueTestId: 'perimeter-value-display',
          },
        ],
        testId: 'area-values',
      },
    ];
  });

  return (
    <Show when={areaData()}>
      {(data) => (
        <div
          class="flex flex-col gap-3 p-3"
          data-testid="area-measurement-details"
        >
          <MeasurementProperty
            icon="tc-icon-measure-area"
            title={t('measurements.2D')}
            resultRows={data()}
            testId="area-section"
            infoButtonConfig={props.infoButtonConfig}
          />
        </div>
      )}
    </Show>
  );
};
